import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Archive, ArchiveRestore, Loader2, Search, FileText, FolderKanban, Eye, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { ro } from "date-fns/locale";
import { API } from "../config/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";

export default function ArchivedCasesPage() {
  const { getAuthHeader } = useAuth();
  const [archivedCases, setArchivedCases] = useState([]);
  const [archivedCasesBC, setArchivedCasesBC] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [tab, setTab] = useState("all"); // "all", "dosare", "bc"
  const [unarchiveDialog, setUnarchiveDialog] = useState(null);
  const [viewCase, setViewCase] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [sortField, setSortField] = useState('numar_intern');
  const [sortDirection, setSortDirection] = useState('asc');

  const fetchData = useCallback(async () => {
    try {
      const [casesRes, casesBCRes] = await Promise.all([
        axios.get(`${API}/cases/archived/list`, getAuthHeader()),
        axios.get(`${API}/cases-bc/archived/list`, getAuthHeader()),
      ]);
      setArchivedCases(casesRes.data);
      setArchivedCasesBC(casesBCRes.data);
    } catch (error) {
      console.error("Error fetching archived cases:", error);
      toast.error("Eroare la încărcarea dosarelor arhivate");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUnarchive = async () => {
    if (!unarchiveDialog) return;
    const { id, type } = unarchiveDialog;
    try {
      const endpoint = type === "case" ? `${API}/cases/${id}/unarchive` : `${API}/cases-bc/${id}/unarchive`;
      await axios.patch(endpoint, {}, getAuthHeader());
      toast.success("Dosar dezarhivat cu succes");
      setUnarchiveDialog(null);
      fetchData();
    } catch (error) {
      toast.error("Eroare la dezarhivarea dosarului");
    }
  };

  const handleView = async (item) => {
    setViewLoading(true);
    setViewCase(null);
    try {
      const endpoint = item._type === "case" ? `${API}/cases/${item.id}` : `${API}/cases-bc/${item.id}`;
      const res = await axios.get(endpoint, getAuthHeader());
      setViewCase({ ...res.data, _type: item._type });
    } catch (error) {
      toast.error("Eroare la încărcarea dosarului");
    } finally {
      setViewLoading(false);
    }
  };

  const normalizeSearch = (str) => (str || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  // Combine and filter
  const allItems = [
    ...archivedCases.map(c => ({ ...c, _type: "case" })),
    ...archivedCasesBC.map(c => ({ ...c, _type: "case_bc" })),
  ];

  const extractSortNum = (intern) => {
    if (!intern) return 99999;
    const match = intern.match(/^(\d+)/);
    return match ? parseInt(match[1]) : 99999;
  };

  const getClientName = (item) => {
    return (item.clients || []).map(c => c.display_name).join(", ") || "";
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-primary" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-primary" />;
  };

  const filtered = allItems
    .filter(item => {
      if (tab === "dosare") return item._type === "case";
      if (tab === "bc") return item._type === "case_bc";
      return true;
    })
    .filter(item => {
      if (!searchQuery.trim()) return true;
      const q = normalizeSearch(searchQuery);
      const clientNames = (item.clients || []).map(c => c.display_name).join(" ");
      return (
        normalizeSearch(item.numar_dosar).includes(q) ||
        normalizeSearch(item.numar_dosar_intern).includes(q) ||
        normalizeSearch(clientNames).includes(q)
      );
    })
    .sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;
      
      if (sortField === 'numar_intern') {
        const numA = extractSortNum(a.numar_dosar_intern);
        const numB = extractSortNum(b.numar_dosar_intern);
        if (numA !== numB) return (numA - numB) * dir;
        return getClientName(a).localeCompare(getClientName(b), 'ro');
      }
      
      if (sortField === 'numar_dosar') {
        return (a.numar_dosar || "").localeCompare(b.numar_dosar || "", 'ro') * dir;
      }
      
      if (sortField === 'client') {
        return getClientName(a).localeCompare(getClientName(b), 'ro') * dir;
      }
      
      if (sortField === 'archived_at') {
        return ((a.archived_at || "").localeCompare(b.archived_at || "")) * dir;
      }
      
      if (sortField === 'instanta') {
        return ((a.instanta || "").localeCompare(b.instanta || "", 'ro')) * dir;
      }
      
      if (sortField === 'etapa') {
        return ((a.etapa_procesuala || "").localeCompare(b.etapa_procesuala || "", 'ro')) * dir;
      }
      
      return extractSortNum(a.numar_dosar_intern) - extractSortNum(b.numar_dosar_intern);
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="archived-cases-page">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Archive className="w-6 h-6" />
            Dosare Arhivate
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            Dosare finalizate mutate din secțiunile active
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{archivedCases.length} Dosare</Badge>
          <Badge variant="secondary">{archivedCasesBC.length} Dosare BC</Badge>
        </div>
      </div>

      {/* Search + Tabs */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Caută după nr. dosar, nr. intern sau client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="archived-search-input"
          />
        </div>
        {[
          { key: "all", label: "Toate", icon: null },
          { key: "dosare", label: "Dosare", icon: FileText },
          { key: "bc", label: "Dosare BC", icon: FolderKanban },
        ].map(f => (
          <Button
            key={f.key}
            variant={tab === f.key ? "default" : "outline"}
            size="sm"
            onClick={() => setTab(f.key)}
            data-testid={`filter-archived-${f.key}-btn`}
          >
            {f.icon && <f.icon className="w-3.5 h-3.5 mr-1.5" />}
            {f.label}
          </Button>
        ))}
      </div>
      </div>{/* End Sticky Header */}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">
            Dosare Arhivate
            <Badge variant="secondary" className="ml-2">{filtered.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="font-body text-muted-foreground">
                {searchQuery.trim() ? "Nu s-au găsit rezultate." : "Nu există dosare arhivate."}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60px]">Tip</TableHead>
                    <TableHead className="cursor-pointer hover:text-primary select-none" onClick={() => handleSort('numar_intern')}>
                      <span className="flex items-center">Nr. Intern<SortIcon field="numar_intern" /></span>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:text-primary select-none" onClick={() => handleSort('numar_dosar')}>
                      <span className="flex items-center">Nr. Dosar<SortIcon field="numar_dosar" /></span>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:text-primary select-none" onClick={() => handleSort('client')}>
                      <span className="flex items-center">Client<SortIcon field="client" /></span>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:text-primary select-none" onClick={() => handleSort('etapa')}>
                      <span className="flex items-center">Etapă<SortIcon field="etapa" /></span>
                    </TableHead>
                    <TableHead>Avocat</TableHead>
                    <TableHead className="text-center bg-amber-50/60 cursor-pointer hover:text-primary select-none" onClick={() => handleSort('archived_at')}>
                      <span className="flex items-center justify-center">Data Arhivării<SortIcon field="archived_at" /></span>
                    </TableHead>
                    <TableHead className="w-[100px] text-center">Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((item) => {
                    const clientNames = (item.clients || []).map(c => c.display_name).join(", ") || "—";
                    return (
                      <TableRow key={`${item._type}-${item.id}`} data-testid={`archived-item-${item.id}`}>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={item._type === "case_bc" ? "text-purple-600 border-purple-200 bg-purple-50" : "text-blue-600 border-blue-200 bg-blue-50"}
                          >
                            {item._type === "case_bc" ? "BC" : "Dosar"}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium text-blue-600">
                          {item.numar_dosar_intern || "—"}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {item.numar_dosar || "—"}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{clientNames}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">{item.etapa_procesuala || "—"}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{item.avocat_repartizat_name || "—"}</TableCell>
                        <TableCell className="text-center bg-amber-50/40">
                          {item.archived_at ? (
                            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 font-mono text-xs">
                              {format(new Date(item.archived_at), "dd.MM.yyyy", { locale: ro })}
                            </Badge>
                          ) : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 justify-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleView(item)}
                              data-testid={`view-archived-${item.id}`}
                              title="Vizualizare"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                              onClick={() => setUnarchiveDialog({
                                id: item.id,
                                type: item._type,
                                label: item.numar_dosar_intern || item.numar_dosar
                              })}
                              data-testid={`unarchive-${item.id}`}
                              title="Dezarhivează"
                            >
                              <ArchiveRestore className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* View Case Dialog */}
      <Dialog open={!!viewCase || viewLoading} onOpenChange={(open) => { if (!open) { setViewCase(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="view-archived-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              {viewCase ? `${viewCase.numar_dosar_intern || ""} — ${viewCase.numar_dosar || ""}` : "Se încarcă..."}
            </DialogTitle>
          </DialogHeader>
          {viewLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : viewCase && (
            <div className="space-y-4">
              {/* Info grid */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Nr. Intern</Label>
                  <p className="font-medium">{viewCase.numar_dosar_intern || "—"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Nr. Dosar</Label>
                  <p className="font-mono">{viewCase.numar_dosar || "—"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Client</Label>
                  <p>{(viewCase.clients || []).map(c => c.display_name).join(", ") || "—"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Instanță</Label>
                  <p>{viewCase.instanta || "—"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Etapă Procesuală</Label>
                  <Badge variant="secondary">{viewCase.etapa_procesuala || "—"}</Badge>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Obiect</Label>
                  <p>{viewCase.obiect || "—"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Materie</Label>
                  <p>{viewCase.materie || "—"}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Parte Adversă</Label>
                  <p>{(viewCase.parte_adversa || []).join(", ") || "—"}</p>
                </div>
                {viewCase.archived_at && (
                  <div className="col-span-2">
                    <Label className="text-xs text-muted-foreground">Data Arhivării</Label>
                    <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 ml-1">
                      {format(new Date(viewCase.archived_at), "dd.MM.yyyy HH:mm", { locale: ro })}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Observații */}
              {viewCase.observatii && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <Label className="text-xs text-muted-foreground block mb-1">Observații</Label>
                  <p className="text-sm whitespace-pre-wrap">{viewCase.observatii}</p>
                </div>
              )}

              {/* Termene */}
              {viewCase.termene_judecata?.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">
                    Termene Judecată ({viewCase.termene_judecata.length})
                  </Label>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {[...viewCase.termene_judecata]
                      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
                      .map((term, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-sm p-2 rounded bg-muted/30">
                          <Badge variant="outline" className="font-mono text-xs shrink-0">
                            {term.date ? format(new Date(term.date), "dd.MM.yyyy") : "—"}
                          </Badge>
                          <span className="text-muted-foreground truncate">{term.description || "—"}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Soluții */}
              {viewCase.solutii_finale && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Label className="text-xs text-green-700 block mb-1">Soluție Finală</Label>
                  <p className="text-sm">{viewCase.solutii_finale}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Unarchive Confirmation */}
      <AlertDialog open={!!unarchiveDialog} onOpenChange={(open) => !open && setUnarchiveDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dezarhivează dosarul?</AlertDialogTitle>
            <AlertDialogDescription>
              Dosarul <strong>{unarchiveDialog?.label}</strong> va fi mutat înapoi în secțiunea activă.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnarchive}>
              Dezarhivează
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
